import type {
  CancellationFormValues,
  CancellationStep,
  CancellationSubmissionLock,
} from "@replohq/agent-billing/cancellation/cancellationFlow";

import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  CANCELLATION_FORM_DEFAULT_VALUES,
  CANCELLATION_REASONS,
  cancellationFormSchema,
  formatCancellationEndDate,
  getCancellationFeedbackField,
  getCancellationFeedbackHint,
  getCancellationRetentionOffer,
  getNextCancellationStep,
  getStarterKeeps,
  isUsageWithinPlan,
  submitCancellationOnce,
} from "@replohq/agent-billing/cancellation/cancellationFlow";
import { LabeledControl } from "@replohq/lattice/blocks/labeled-control/LabeledControl";
import { ResponsiveDialog } from "@replohq/lattice/blocks/modal/ResponsiveDialog";
import { SelectionItem } from "@replohq/lattice/blocks/selection/SelectionItem";
import { Button } from "@replohq/lattice/components/button/Button";
import { Checkbox } from "@replohq/lattice/components/checkbox/Checkbox";
import { FieldError, FieldSet } from "@replohq/lattice/components/field/Field";
import { RadioGroup } from "@replohq/lattice/components/radio-group/RadioGroup";
import { Separator } from "@replohq/lattice/components/separator/Separator";
import { Textarea } from "@replohq/lattice/components/textarea/Textarea";
import { useIsMobile } from "@replohq/lattice/hooks/use-mobile";
import { DURATION, EASING } from "@replohq/lattice/utils/motion";
import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { useAgentLaunch } from "@v2/features/agent-beta/AgentLaunchProvider";
import useApplySubscriptionChangeMutation from "@v2/features/billing/hooks/useApplySubscriptionChangeMutation";
import { CancellationAgentOffer } from "@v2/features/billing/modals/CancellationAgentOffer";
import { PlanChangeConfirmationModal } from "@v2/features/billing/modals/PlanChangeConfirmationModal";
import {
  getActiveSubscriptionPricePerMonth,
  getEstimatedPlanPricePerMonth,
} from "@v2/features/billing/planPriceUtils";
import { analytics } from "@v2/features/infra/analytics/tracking";
import { useSubscriptionDetails } from "@v2/hooks/subscription";
import { billingRouter } from "@v2/trpc/routes/billing";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import { Check } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Controller, useForm } from "react-hook-form";
import { DEPRECATED_formatPriceInUsdDollars } from "replo-utils/lib/format";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { getBillingPlanInfoByTier } from "schemas/billing";

type FooterAction = {
  label: string;
  onClick: () => void;
  isDestructive?: boolean;
  isDisabled?: boolean;
};

interface CancelPlanModalProps {
  source: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CancelPlanModal: React.FC<CancelPlanModalProps> = ({
  source,
  open,
  onOpenChange,
}) => {
  const workspaceId = useCurrentWorkspaceId();
  const { data: activeSubscription } = useSubscriptionDetails();
  const agentLaunch = useAgentLaunch();
  const shouldReduceMotion = useReducedMotion();
  const isMobile = useIsMobile();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    trigger,
    getValues,
    watch,
    reset,
  } = useForm<CancellationFormValues>({
    resolver: zodResolver(cancellationFormSchema),
    mode: "onBlur",
    defaultValues: CANCELLATION_FORM_DEFAULT_VALUES,
  });

  const { applySubscriptionChange } = useApplySubscriptionChangeMutation();
  const submissionLockRef = React.useRef<CancellationSubmissionLock>({
    isSubmitting: false,
  });

  const [currentStep, setCurrentStep] =
    React.useState<CancellationStep>("reasons");
  const [isPlanChangeModalOpen, setIsPlanChangeModalOpen] =
    React.useState(false);
  const [hasAcknowledgedLoss, setHasAcknowledgedLoss] = React.useState(false);

  const selectedReason = watch("reason") ?? null;
  const feedbackField = getCancellationFeedbackField(selectedReason);
  const feedbackHint = getCancellationFeedbackHint({
    reason: selectedReason,
    feedback: watch("feedback"),
  });
  // Every event carries whatever the customer has typed so far, even when an offer retains them.
  const getFeedback = () => getValues("feedback").trim();

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setCurrentStep("reasons");
      setHasAcknowledgedLoss(false);
      reset();
    }
    onOpenChange(isOpen);
  };

  const { data: nextBillingDate } =
    billingRouter.getNextCorePlanBillingDate.useQuery(
      workspaceId && activeSubscription?.id
        ? {
            workspaceId,
            subscriptionId: activeSubscription.id,
          }
        : skipToken,
    );

  const formattedEndDate = formatCancellationEndDate(nextBillingDate);
  const { data: publishedElementsUsage } =
    workspaceRouter.getPublishedElementsUsage.useQuery(
      workspaceId ?? skipToken,
    );
  const { data: shopifyThemeProjectsUsage } =
    workspaceRouter.getShopifyThemeProjectsUsage.useQuery(
      workspaceId ?? skipToken,
    );

  // NOTE (Cole 2025-09-11): There must be an active subscription to submit an update.
  if (!activeSubscription || !workspaceId) {
    return null;
  }

  const currentPlan = getBillingPlanInfoByTier(activeSubscription.billingTier);
  const starterPlan = getBillingPlanInfoByTier("starter");
  const nameOfPlanBeingCancelled = currentPlan.displayName;
  const retentionOffer = getCancellationRetentionOffer({
    currentTier: activeSubscription.billingTier,
    reason: selectedReason,
    isAgentAvailable: agentLaunch?.isAvailable ?? false,
    isDowngradeAvailable:
      activeSubscription.paymentProcessor === "shopify" &&
      isUsageWithinPlan({
        plan: starterPlan,
        usage: {
          publishedElements: publishedElementsUsage?.current,
          themeProjects: shopifyThemeProjectsUsage?.current,
        },
      }),
  });
  const onSubmit = async (formData: CancellationFormValues) => {
    if (currentStep !== "confirmation") {
      return;
    }

    const didSubmit = await submitCancellationOnce({
      submissionLock: submissionLockRef.current,
      submit: async () => {
        analytics.logEvent("billing.cancelModal.submittedFeedbackStep", {
          currentPlan: activeSubscription.billingTier,
          feedback: formData.feedback.trim(),
        });

        await applySubscriptionChange({
          workspaceId,
          billingTier: "free",
          billingInterval: activeSubscription.billingInterval,
          source,
          addOns: [],
        });
      },
    });

    if (didSubmit) {
      handleOpenChange(false);
    }
  };

  const formSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (currentStep === "confirmation") {
      void handleSubmit(onSubmit)(event);
    }
  };

  const goToStep = (nextStep: CancellationStep | null) => {
    if (nextStep) {
      setCurrentStep(nextStep);
    }
  };

  const continueCancellation = () => {
    if (retentionOffer === "agent") {
      analytics.logEvent("billing.cancelModal.agentUpsellDeclined", {
        currentPlan: activeSubscription.billingTier,
      });
    }
    if (retentionOffer === "starterDowngrade" && selectedReason) {
      analytics.logEvent("billing.cancelModal.downgradeDeclined", {
        currentPlan: activeSubscription.billingTier,
        reason: selectedReason,
      });
    }
    goToStep(getNextCancellationStep({ currentStep, retentionOffer }));
  };

  const acceptStarterDowngrade = () => {
    if (!selectedReason) {
      return;
    }
    analytics.logEvent("billing.cancelModal.downgradeAccepted", {
      currentPlan: activeSubscription.billingTier,
      reason: selectedReason,
      feedback: getFeedback(),
    });
    handleOpenChange(false);
    setIsPlanChangeModalOpen(true);
  };

  const acceptAgentOffer = () => {
    if (!agentLaunch?.isAvailable) {
      return;
    }
    analytics.logEvent("billing.cancelModal.agentUpsellAccepted", {
      currentPlan: activeSubscription.billingTier,
      reason: selectedReason ?? undefined,
      feedback: getFeedback(),
    });
    handleOpenChange(false);
    void agentLaunch.launchProject();
  };

  const keepCurrentPlan = () => {
    analytics.logEvent("billing.cancelModal.retained", {
      currentPlan: activeSubscription.billingTier,
      step: currentStep,
      reason: selectedReason ?? undefined,
      feedback: getFeedback(),
    });
    handleOpenChange(false);
  };

  const submitReasonStep = () => {
    void trigger().then((isValid) => {
      if (!isValid) {
        return;
      }
      const reason = getValues("reason");
      analytics.logEvent("billing.cancelModal.submittedReasonStep", {
        currentPlan: activeSubscription.billingTier,
        reason,
        feedback: getFeedback(),
      });
      if (retentionOffer === "starterDowngrade") {
        analytics.logEvent("billing.cancelModal.downgradeOffered", {
          currentPlan: activeSubscription.billingTier,
          reason,
        });
      }
      if (retentionOffer === "agent") {
        analytics.logEvent("billing.cancelModal.agentUpsellViewed", {
          currentPlan: activeSubscription.billingTier,
        });
      }
      goToStep(getNextCancellationStep({ currentStep, retentionOffer }));
    });
  };

  const starterPricePerMonth = getEstimatedPlanPricePerMonth({
    billingTier: "starter",
    billingInterval: activeSubscription.billingInterval,
    addOns: [],
  });
  // Product units carry add-ons; a subscription without priced units falls back to the plan's list price.
  const currentPricePerMonth =
    getActiveSubscriptionPricePerMonth({ activeSubscription }) ||
    getEstimatedPlanPricePerMonth({
      billingTier: activeSubscription.billingTier,
      billingInterval: activeSubscription.billingInterval,
      addOns: [],
    });
  const starterPriceSuffix =
    activeSubscription.billingInterval === "yearly"
      ? " a month, billed annually"
      : " a month";
  const starterPriceLabel = DEPRECATED_formatPriceInUsdDollars({
    amount: starterPricePerMonth,
    currency: "usd",
  });
  const currentPriceLabel = DEPRECATED_formatPriceInUsdDollars({
    amount: currentPricePerMonth,
    currency: "usd",
  });
  const starterKeeps = getStarterKeeps({
    starterPlan,
    freePlan: getBillingPlanInfoByTier("free"),
  });

  const stepTitle = exhaustiveSwitch(currentStep)({
    reasons: () => "Why are you canceling?",
    retention: () => {
      return exhaustiveSwitch(retentionOffer ?? "agent")({
        starterDowngrade: () => "Keep your pages live on Starter",
        agent: () => "Introducing the new version of Replo",
      });
    },
    confirmation: () => `Cancel your ${nameOfPlanBeingCancelled} plan?`,
  });

  const keepAction: FooterAction = {
    label: "Keep my subscription for now",
    onClick: keepCurrentPlan,
  };
  const actions: { primary: FooterAction; secondary: FooterAction } =
    exhaustiveSwitch(currentStep)({
      reasons: () => ({
        primary: { label: "Continue", onClick: submitReasonStep },
        secondary: keepAction,
      }),
      retention: () => ({
        primary: exhaustiveSwitch(retentionOffer ?? "agent")({
          starterDowngrade: () => ({
            label: `Switch to Starter for ${starterPriceLabel}${starterPriceSuffix}`,
            onClick: acceptStarterDowngrade,
          }),
          agent: () => ({
            label: "Enable Replo Agent",
            onClick: acceptAgentOffer,
          }),
        }),
        secondary: {
          label: "Continue cancellation",
          onClick: continueCancellation,
        },
      }),
      // Keeping the plan is the primary action on the last screen; cancelling needs the acknowledgement.
      confirmation: () => ({
        primary: keepAction,
        secondary: {
          label: "Cancel plan",
          onClick: () => void handleSubmit(onSubmit)(),
          isDestructive: true,
          isDisabled: !hasAcknowledgedLoss,
        },
      }),
    });
  const footerButtonClassName = "h-11 w-full md:h-8 md:w-auto";
  const primaryButton = (
    <Button
      key="primary"
      type="button"
      size="base"
      className={footerButtonClassName}
      onClick={actions.primary.onClick}
      disabled={isSubmitting}
    >
      {actions.primary.label}
    </Button>
  );
  const secondaryButton = (
    <Button
      key="secondary"
      type="button"
      variant={actions.secondary.isDestructive ? "destructive" : "secondary"}
      size="base"
      className={footerButtonClassName}
      onClick={actions.secondary.onClick}
      loading={actions.secondary.isDestructive && isSubmitting}
      disabled={isSubmitting || actions.secondary.isDisabled}
    >
      {actions.secondary.label}
    </Button>
  );
  // Two buttons on every step; closing the dialog is how a customer starts over.
  const footerButtons = isMobile
    ? [primaryButton, secondaryButton]
    : [secondaryButton, primaryButton];

  // The Agent pitch carries its own heading, the same one the launch modal shows.
  const showStepHeading = !(
    currentStep === "retention" && retentionOffer === "agent"
  );

  return (
    <>
      <form onSubmit={formSubmit}>
        <ResponsiveDialog
          open={open}
          onOpenChange={(isOpen) => {
            if (
              !isOpen &&
              !isSubmitting &&
              !submissionLockRef.current.isSubmitting
            ) {
              handleOpenChange(false);
            }
          }}
          accessibleTitle={stepTitle}
          // ResponsiveDialog does not thread the LayoutClassName generic, so a literal width cannot use className.
          UNSAFE_className="sm:max-w-xl"
          footer={
            <div className="flex w-full flex-col gap-2 md:flex-row md:items-center md:justify-end md:gap-3">
              {footerButtons}
            </div>
          }
        >
          <div className="min-h-0 overflow-y-auto">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={currentStep}
                initial={shouldReduceMotion ? false : { opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={shouldReduceMotion ? undefined : { opacity: 0, x: -8 }}
                transition={{ duration: DURATION.fast, ease: EASING.outQuint }}
                className="flex flex-col gap-4"
              >
                {/* The dialog is already named by `accessibleTitle`, so this
                    visible heading would otherwise be announced twice. */}
                {showStepHeading && (
                  <h2
                    aria-hidden
                    className="pr-10 text-lg font-semibold leading-tight tracking-tight text-default"
                  >
                    {stepTitle}
                  </h2>
                )}

                {currentStep === "reasons" && (
                  <>
                    <img
                      src="/images/billing/cancel-plan-sad-globes.png"
                      alt=""
                      className="mx-auto max-h-24"
                    />
                    <FieldSet
                      aria-invalid={Boolean(errors.reason)}
                      aria-describedby={
                        errors.reason ? "cancel-reason-error" : undefined
                      }
                    >
                      <Controller
                        name="reason"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <RadioGroup
                            value={value ?? null}
                            onValueChange={(nextValue) => {
                              const match = CANCELLATION_REASONS.find(
                                (reason) => reason.value === nextValue,
                              );
                              if (!match) {
                                return;
                              }
                              onChange(match.value);
                              // Radios never blur in a way the customer would recognise, so
                              // the choice itself clears the error the Continue button
                              // raised. A text error already showing is rechecked against
                              // the new reason's minimum.
                              void trigger(
                                errors.feedback
                                  ? ["reason", "feedback"]
                                  : "reason",
                              );
                            }}
                          >
                            <div className="grid gap-2 sm:grid-cols-2">
                              {CANCELLATION_REASONS.map((reason) => (
                                <SelectionItem
                                  key={reason.value}
                                  type="radio"
                                  variant="outline"
                                  id={reason.value}
                                  label={reason.label}
                                />
                              ))}
                            </div>
                          </RadioGroup>
                        )}
                      />
                      <FieldError
                        id="cancel-reason-error"
                        errors={[errors.reason]}
                      />
                    </FieldSet>

                    <Controller
                      name="feedback"
                      control={control}
                      render={({ field }) => (
                        <LabeledControl
                          id="cancel-plan-feedback"
                          label={feedbackField.label}
                          required
                          description={
                            feedbackHint && (
                              <span id="cancel-plan-feedback-hint">
                                {feedbackHint}
                              </span>
                            )
                          }
                          error={errors.feedback?.message}
                        >
                          <Textarea
                            {...field}
                            // Clear the nudge while typing: if it only cleared on blur, the dialog would shrink under the Continue click and swallow it.
                            onChange={(event) => {
                              field.onChange(event);
                              if (errors.feedback) {
                                void trigger("feedback");
                              }
                            }}
                            id="cancel-plan-feedback"
                            aria-required
                            aria-describedby={
                              feedbackHint
                                ? "cancel-plan-feedback-hint"
                                : undefined
                            }
                            size="base"
                            variant="long"
                            invalid={Boolean(errors.feedback)}
                            placeholder={feedbackField.placeholder}
                          />
                        </LabeledControl>
                      )}
                    />
                  </>
                )}

                {currentStep === "retention" &&
                  retentionOffer === "starterDowngrade" && (
                    <div className="flex flex-col gap-4">
                      <p className="text-sm leading-relaxed text-default">
                        On Free, pages published through Replo on your Shopify
                        theme stop working after 14 days, and your custom
                        domains and A/B tests go with them.
                      </p>
                      <div className="flex flex-col gap-2 rounded-lg bg-surface px-4 py-3">
                        <p className="text-sm font-medium text-default">
                          Starter keeps what you've built for{" "}
                          {starterPriceLabel}
                          {starterPriceSuffix} instead of {currentPriceLabel}
                        </p>
                        <ul className="flex flex-col gap-1.5">
                          {starterKeeps.map((keep) => (
                            <li
                              key={keep}
                              className="flex items-center gap-2 text-sm text-muted"
                            >
                              <Check
                                className="size-4 shrink-0 text-default"
                                aria-hidden
                              />
                              {keep}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                {currentStep === "retention" && retentionOffer === "agent" && (
                  <CancellationAgentOffer />
                )}

                {currentStep === "confirmation" && (
                  <div className="flex flex-col gap-4">
                    <p className="text-sm leading-relaxed text-default">
                      Your {nameOfPlanBeingCancelled} plan stays active until{" "}
                      {formattedEndDate}. After that, this workspace moves to
                      the Free plan.
                    </p>
                    <img
                      src="/images/billing/cancel-plan-sad-balloons.png"
                      alt=""
                      className="mx-auto max-h-24"
                    />
                    <Separator />
                    <label
                      htmlFor="cancel-plan-acknowledge"
                      className="flex cursor-pointer items-start gap-3 rounded-lg bg-surface px-3 py-3 text-sm leading-relaxed text-default"
                    >
                      <Checkbox
                        id="cancel-plan-acknowledge"
                        checked={hasAcknowledgedLoss}
                        onCheckedChange={(checked) => {
                          setHasAcknowledgedLoss(checked === true);
                        }}
                        className="mt-0.5"
                      />
                      <span>
                        I understand that by canceling my Replo plan, any pages
                        or sites published through Replo on my Shopify theme
                        will no longer work after 14 days, and any assets
                        uploaded to Replo will no longer be accessible.
                      </span>
                    </label>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </ResponsiveDialog>
      </form>
      <PlanChangeConfirmationModal
        open={isPlanChangeModalOpen}
        onOpenChange={setIsPlanChangeModalOpen}
        newTier="starter"
        newBillingInterval={activeSubscription.billingInterval}
        addOns={[]}
      />
    </>
  );
};
